import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../store/auth.ts';
import { useUi } from '../../store/ui.ts';
import { search } from '../../api/auth.ts';
import { initialsOf, parentPath } from '../../lib/format.ts';
import { FileIcon } from '../files/FileIcon.tsx';
import { NotificationBell } from './NotificationBell.tsx';
import { IconCloud, IconSearch } from '../../lib/icons.tsx';
import type { ApiNode } from '../../api/types.ts';
import type { SocketState } from '../../hooks/useSocket.ts';

const LABEL: Record<SocketState, string> = {
  connecting: '연결 중', online: '동기화됨', offline: '연결 끊김',
};

export function TopBar({ socketState }: { socketState: SocketState }) {
  const user = useAuth((s) => s.user);
  const namespaces = useAuth((s) => s.namespaces);
  const logout = useAuth((s) => s.logout);
  const toggleSidebar = useUi((s) => s.toggleSidebar);
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ApiNode[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const nsId = namespaces[0]?.id;

  useEffect(() => {
    if (!nsId || query.trim().length < 1) { setResults([]); return; }
    const timer = window.setTimeout(() => {
      search(nsId, query.trim())
        .then((r) => { setResults(r.items); })
        .catch(() => { setResults([]); });
    }, 300);
    return () => { window.clearTimeout(timer); };
  }, [query, nsId]);

  useEffect(() => {
    const onClick = (e: MouseEvent): void => {
      if (!boxRef.current?.contains(e.target as Node)) { setResults([]); setMenuOpen(false); }
    };
    window.addEventListener('mousedown', onClick);
    return () => { window.removeEventListener('mousedown', onClick); };
  }, []);

  const openResult = (node: ApiNode): void => {
    const target = node.type === 'folder' ? node.path : parentPath(node.path);
    navigate(`/files${target}`);
    setQuery('');
    setResults([]);
  };

  return (
    <header className="topbar" ref={boxRef}>
      <button className="btn btn--ghost btn--icon" onClick={toggleSidebar} aria-label="메뉴">
        ☰
      </button>
      <Link to="/files" className="topbar__logo">
        <IconCloud size={22} /> YumSync
      </Link>

      <div className="topbar__search">
        <IconSearch size={16} />
        <input
          className="input"
          placeholder="파일 검색"
          value={query}
          onChange={(e) => { setQuery(e.target.value); }}
          aria-label="파일 검색"
        />
        {results.length > 0 && (
          <div className="search-results">
            {results.map((node) => (
              <button
                key={node.id}
                className="search-results__item"
                onClick={() => { openResult(node); }}
              >
                <FileIcon node={node} />
                <span className="truncate spacer" style={{ textAlign: 'left' }}>
                  {node.name}
                  <div className="search-results__path truncate">{node.path}</div>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="spacer" />

      <div className="row" title={LABEL[socketState]}>
        <span className="sync-dot" data-state={socketState === 'online' ? 'online' : socketState === 'offline' ? 'offline' : 'syncing'} />
        <span className="muted" style={{ fontSize: 12 }}>{LABEL[socketState]}</span>
      </div>

      <NotificationBell />

      <div style={{ position: 'relative' }}>
        <button
          className="avatar"
          onClick={() => { setMenuOpen(!menuOpen); }}
          aria-label="계정 메뉴"
        >
          {initialsOf(user?.display_name ?? '')}
        </button>
        {menuOpen && (
          <div className="ctxmenu" style={{ right: 0, top: 40, position: 'absolute' }}>
            <div style={{ padding: '8px 12px' }}>
              <div style={{ fontWeight: 600 }}>{user?.display_name}</div>
              <div className="faint" style={{ fontSize: 12 }}>{user?.email}</div>
            </div>
            <div className="ctxmenu__sep" />
            <button className="ctxmenu__item" onClick={() => { navigate('/account'); setMenuOpen(false); }}>
              내 계정
            </button>
            <button className="ctxmenu__item" onClick={() => { navigate('/devices'); setMenuOpen(false); }}>
              연결된 PC
            </button>
            <a className="ctxmenu__item" href="/download">앱 다운로드</a>
            <button className="ctxmenu__item" onClick={() => { void logout(); }}>
              로그아웃
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
